import {
    Baby,
    Bookmark,
    BookOpen,
    ChevronRight,
    GraduationCap,
    Heart,
    Puzzle,
    School,
    Search,
    Sparkles,
    Stethoscope,
    Users,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import ROUTES from '@/ROUTES'
import { usePaywallModal } from '@/stripe/PayWallModal'
import { usePaywall } from '@/stripe/PayWallProvider'

import { IPublicResource } from '@/MODELS/publicResource.model'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'
import { useResource } from '@/services/resource.service'

import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'

const categoryColors: Record<string, { bg: string; text: string }> = {
    'Early Developmental Concerns': { bg: 'bg-teal-100', text: 'text-teal-700' },
    'Autism 101 for Newly Diagnosed': { bg: 'bg-purple-100', text: 'text-purple-700' },
    'Understanding Diagnosis': { bg: 'bg-green-100', text: 'text-green-700' },
    'Supporting Your Child': { bg: 'bg-pink-100', text: 'text-pink-700' },
    'Treatments & Therapies': { bg: 'bg-orange-100', text: 'text-orange-700' },
    'School & Advocacy': { bg: 'bg-blue-100', text: 'text-blue-700' },
    'Support for Families': { bg: 'bg-indigo-100', text: 'text-indigo-700' },
}
const defaultCategoryColor = { bg: 'bg-teal-100', text: 'text-teal-700' }

const categoryConfig = [
    {
        title: 'My Saved Resources',
        icon: Bookmark,
        bgGradient: 'bg-gradient-to-br from-amber-50 to-amber-100',
        iconColor: 'text-amber-600',
        hoverShadow: 'hover:shadow-amber-200',
        href: '/saved-resources',
        param: false,
    },
    {
        title: 'Early Developmental Concerns',
        icon: Baby,
        bgGradient: 'bg-gradient-to-br from-teal-50 to-teal-100',
        iconColor: 'text-teal-600',
        hoverShadow: 'hover:shadow-teal-200',
        param: 'Early Developmental Concerns',
    },
    {
        title: 'Autism 101 for Newly Diagnosed',
        icon: GraduationCap,
        bgGradient: 'bg-gradient-to-br from-purple-50 to-purple-100',
        iconColor: 'text-purple-600',
        hoverShadow: 'hover:shadow-purple-200',
        param: 'Autism 101 for Newly Diagnosed',
    },
    {
        title: 'Understanding Diagnosis',
        icon: Puzzle,
        bgGradient: 'bg-gradient-to-br from-green-50 to-green-100',
        iconColor: 'text-green-600',
        hoverShadow: 'hover:shadow-green-200',
        param: 'Understanding Diagnosis',
    },
    {
        title: 'Supporting Your Child',
        icon: Heart,
        bgGradient: 'bg-gradient-to-br from-pink-50 to-pink-100',
        iconColor: 'text-pink-600',
        hoverShadow: 'hover:shadow-pink-200',
        param: 'Supporting Your Child',
    },
    {
        title: 'Treatments & Therapies',
        icon: Stethoscope,
        bgGradient: 'bg-gradient-to-br from-orange-50 to-orange-100',
        iconColor: 'text-orange-600',
        hoverShadow: 'hover:shadow-orange-200',
        param: 'Treatments & Therapies',
    },
    {
        title: 'School & Advocacy',
        icon: School,
        bgGradient: 'bg-gradient-to-br from-blue-50 to-blue-100',
        iconColor: 'text-blue-600',
        hoverShadow: 'hover:shadow-blue-200',
        param: 'School & Advocacy',
    },
    {
        title: 'Support for Families',
        icon: Users,
        bgGradient: 'bg-gradient-to-br from-indigo-50 to-indigo-100',
        iconColor: 'text-indigo-600',
        hoverShadow: 'hover:shadow-indigo-200',
        param: 'Support for Families',
    },
    {
        title: 'All Resources',
        icon: BookOpen,
        bgGradient: 'bg-gradient-to-br from-gray-50 to-gray-100',
        iconColor: 'text-gray-600',
        hoverShadow: 'hover:shadow-gray-200',
        href: '/resource-library',
        param: false,
    },
]

export default function CategoryTiles() {
    const [resources, setResources, ResourceResource] = useResource<IPublicResource[]>(ROUTES.app.publicResource)
    const { user } = useAuth()
    const navigate = useNavigate()
    const { hasAccess, isLoading } = usePaywall()
    const { showSubscriptionModal } = usePaywallModal()

    const [recommendedArticles, setRecommendedArticles] = useState<IPublicResource[]>([])
    const [loadingRecommended, setLoadingRecommended] = useState(true)

    useEffect(() => {
        fetchRecommendedArticles()
    }, [])

    const fetchRecommendedArticles = async () => {
        setLoadingRecommended(true)
        try {
            const result = await api<IPublicResource[]>(ROUTES.app.recommendedResources)
            if (result?.data && Array.isArray(result.data)) {
                setRecommendedArticles(result.data)
            }
        } catch {
            // Non-blocking — just don't show recommendations
        }
        setLoadingRecommended(false)
    }

    function handleArticleClick(resource: IPublicResource) {
        if (resource.link) {
            window.open(resource.link, '_blank', 'noopener,noreferrer')
        } else {
            navigate(`/resource/${resource._id}`)
        }
    }

    if (!resources) {
        return <Loading />
    }
    return (
        <div className='flex-1 w-full min-h-screen pb-8 mx-auto max-w-7xl'>
            <SectionHeader title={`Resources`} subtitle='Get connected to services and essential knowledge' />
            <div className='px-4 py-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                {/* Recommended Articles */}
                {!loadingRecommended && recommendedArticles.length > 0 && (
                    <div className='mb-6'>
                        <div className='flex items-center gap-2 mb-3'>
                            <Sparkles className='w-5 h-5 text-amber-500' />
                            <h3 className='text-base font-semibold text-gray-800'>Recommended Articles</h3>
                        </div>
                        <div className='relative max-w-[calc(100vw-2rem)] sm:max-w-full'>
                            <div className='absolute top-0 bottom-2 right-0 z-10 flex items-center w-8 pointer-events-none bg-gradient-to-l from-gray-50 via-gray-50/80 to-transparent'>
                                <ChevronRight className='w-4 h-4 ml-auto mr-0.5 text-gray-400' />
                            </div>
                            <div className='overflow-x-auto flex gap-3 pb-2 snap-x snap-mandatory scrollbar-hide'>
                                {recommendedArticles.map((article) => {
                                    const colors = categoryColors[article.category || ''] || defaultCategoryColor
                                    return (
                                        <div
                                            key={article._id}
                                            onClick={() => handleArticleClick(article)}
                                            className='flex-shrink-0 w-[200px] snap-start bg-white rounded-2xl border border-gray-100 shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow duration-200'
                                        >
                                            {article.category && (
                                                <span
                                                    className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} mb-2`}
                                                >
                                                    {article.category}
                                                </span>
                                            )}
                                            <h4 className='text-sm font-medium text-gray-900 mb-3 line-clamp-2 leading-snug'>
                                                {article.title}
                                            </h4>
                                            <div className='flex items-center text-xs font-medium text-teal-600'>
                                                Learn More
                                                <ChevronRight className='w-3.5 h-3.5 ml-0.5' />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Tiles */}
                {Array.of(categoryConfig).length === 0 ? (
                    <div className='py-16 text-center bg-white rounded-lg shadow-sm'>
                        <div className='flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full'>
                            <Search className='w-8 h-8 text-gray-400' />
                        </div>
                        <h3 className='mb-2 text-lg font-medium text-gray-900'>No categories found</h3>
                        <p className='text-gray-500'>Try adjusting your search terms</p>
                    </div>
                ) : (
                    <>
                        <div className='grid grid-cols-2 gap-4 mb-8 md:grid-cols-3'>
                            {categoryConfig.map((config, i) => {
                                function handleRedirect(config) {
                                    if (config.href?.includes('providers') && !hasAccess) {
                                        showSubscriptionModal()
                                        return
                                    }

                                    if (!config.param) {
                                        navigate(config.href)
                                    } else {
                                        navigate(`/resource-library?category=${encodeURIComponent(config.param)}`)
                                    }
                                }
                                const IconComponent = config.icon
                                return (
                                    <div
                                        key={i}
                                        className='transition-all duration-200 transform cursor-pointer group hover:scale-105'
                                    >
                                        <div
                                            onClick={() => handleRedirect(config)}
                                            className={`
                      ${config.bgGradient} rounded-3xl  cursor-pointer
                      hover:border-gray-300 ${config.hoverShadow}
                      py-4 px-2 h-full flex flex-col items-center text-center
                      hover:shadow-md transition-all duration-200
                    `}
                                        >
                                            <div
                                                className={`
                        w-16 h-16 rounded-lg flex items-center justify-center

                        group-hover:scale-110 transition-transform duration-200
                      `}
                                            >
                                                <IconComponent className={`w-8 h-8 ${config.iconColor}`} />
                                            </div>

                                            <h3 className='font-normal text-sm max-w-[140px] text-gray-900 mb-2'>
                                                {config.title}
                                            </h3>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
